import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '@/lib/supabase-client';
import { useAuth } from '../hooks/useAuth';
import { HabitDetailModal } from '../components/habits/HabitDetailModal';
import { Plus, ChevronDown, ChevronRight, X, GripVertical, } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable, } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
const colorOptions = [
    { name: 'Orange', value: '#FF8C00' },
    { name: 'Blue', value: '#00B7EB' },
    { name: 'Red', value: '#FF4040' },
    { name: 'Green', value: '#00CD00' },
    { name: 'Purple', value: '#9B30FF' },
    { name: 'Yellow', value: '#FFD700' },
    { name: 'Teal', value: '#00CED1' },
    { name: 'Gray', value: '#808080' }
];
const colorPairs = {
    '#FF8C00': { light: { r: 255, g: 220, b: 120 }, dark: { r: 255, g: 69, b: 0 } },
    '#00B7EB': { light: { r: 135, g: 206, b: 255 }, dark: { r: 0, g: 100, b: 255 } },
    '#FF4040': { light: { r: 255, g: 160, b: 180 }, dark: { r: 220, g: 20, b: 60 } },
    '#00CD00': { light: { r: 144, g: 255, b: 144 }, dark: { r: 50, g: 205, b: 50 } },
    '#9B30FF': { light: { r: 221, g: 160, b: 255 }, dark: { r: 138, g: 43, b: 226 } },
    '#FFD700': { light: { r: 255, g: 255, b: 150 }, dark: { r: 255, g: 215, b: 0 } },
    '#00CED1': { light: { r: 175, g: 255, b: 255 }, dark: { r: 0, g: 206, b: 209 } },
    '#808080': { light: { r: 220, g: 220, b: 220 }, dark: { r: 128, g: 128, b: 128 } }
};
const generateCalendarDates = (numDays = 14) => {
    const dates = [];
    const today = new Date();
    for (let i = numDays - 1; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(today.getDate() - i);
        dates.push({
            date: date.toISOString().split('T')[0],
            day: date.getDate(),
            dayName: date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase(),
            month: date.toLocaleDateString('en-US', { month: 'short' }),
            isToday: i === 0
        });
    }
    return dates;
};
const getColorWithSaturation = (habitColor, habit, targetDate) => {
    const isCompleted = habit.history?.includes(targetDate);
    const isSkipped = habit.skipped?.[targetDate];
    const colorPair = colorPairs[habitColor] || colorPairs['#808080'];
    if (!isCompleted && !isSkipped) {
        return {
            backgroundColor: 'var(--bg-base)',
            borderColor: 'var(--border-color)'
        };
    }
    const allActivityDates = new Set([
        ...(habit.history || []),
        ...Object.keys(habit.skipped || {}).filter((d) => habit.skipped?.[d])
    ]);
    const sortedActivityDates = Array.from(allActivityDates).sort();
    const targetIndex = sortedActivityDates.indexOf(targetDate);
    if (targetIndex === -1) {
        return {
            backgroundColor: 'var(--bg-base)',
            borderColor: 'var(--border-color)'
        };
    }
    let streakStart = targetIndex;
    let streakEnd = targetIndex;
    for (let i = targetIndex - 1; i >= 0; i--) {
        const [y1, m1, d1] = sortedActivityDates[i].split('-').map(Number);
        const [y2, m2, d2] = sortedActivityDates[i + 1].split('-').map(Number);
        const currentDate = new Date(y1, m1 - 1, d1);
        const nextDate = new Date(y2, m2 - 1, d2);
        const dayDiff = (nextDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24);
        if (dayDiff === 1) {
            streakStart = i;
        }
        else {
            break;
        }
    }
    for (let i = targetIndex + 1; i < sortedActivityDates.length; i++) {
        const [y1, m1, d1] = sortedActivityDates[i].split('-').map(Number);
        const [y2, m2, d2] = sortedActivityDates[i - 1].split('-').map(Number);
        const currentDate = new Date(y1, m1 - 1, d1);
        const prevDate = new Date(y2, m2 - 1, d2);
        const dayDiff = (currentDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24);
        if (dayDiff === 1) {
            streakEnd = i;
        }
        else {
            break;
        }
    }
    const streakLength = streakEnd - streakStart + 1;
    const positionInStreak = targetIndex - streakStart + 1;
    const baseProgress = positionInStreak / Math.max(streakLength, 10);
    let progress;
    if (baseProgress <= 0.5) {
        progress = Math.sqrt(baseProgress * 2) * 0.5;
    }
    else {
        const remaining = (baseProgress - 0.5) * 2;
        progress = 0.5 + remaining * remaining * 0.5;
    }
    progress = Math.max(0.2, Math.min(0.95, progress));
    const r = Math.round(colorPair.light.r + (colorPair.dark.r - colorPair.light.r) * progress);
    const g = Math.round(colorPair.light.g + (colorPair.dark.g - colorPair.light.g) * progress);
    const b = Math.round(colorPair.light.b + (colorPair.dark.b - colorPair.light.b) * progress);
    const backgroundColor = `rgb(${r}, ${g}, ${b})`;
    return {
        backgroundColor,
        borderColor: backgroundColor
    };
};
const calculateCurrentStreak = (history, skipped) => {
    const today = new Date().toISOString().split('T')[0];
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];
    const hasActivityToday = history?.includes(today) || skipped?.[today];
    const hasActivityYesterday = history?.includes(yesterdayStr) || skipped?.[yesterdayStr];
    if (!hasActivityToday && !hasActivityYesterday) {
        return 0;
    }
    let streak = 0;
    let currentDate = new Date();
    for (let i = 0; i < 365; i++) {
        const dateStr = currentDate.toISOString().split('T')[0];
        const isCompleted = history?.includes(dateStr);
        const isSkipped = skipped?.[dateStr];
        if (isCompleted || isSkipped) {
            if (isCompleted) {
                streak++;
            }
            currentDate.setDate(currentDate.getDate() - 1);
        }
        else {
            break;
        }
    }
    return streak;
};
function SortableGroupHeader({ category, isExpanded, stats, calendarDates, onToggle }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging, } = useSortable({ id: category.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };
    return (_jsxs("div", { ref: setNodeRef, style: style, className: "flex items-center", ...attributes, children: [_jsxs("button", { onClick: onToggle, className: "w-64 flex-shrink-0 px-4 py-3 flex items-center gap-2 hover:opacity-80 transition-opacity", style: { borderRight: '1px solid var(--border-color)', backgroundColor: 'var(--bg-elevated)' }, children: [_jsx("div", { ...listeners, className: "cursor-grab active:cursor-grabbing", children: _jsx(GripVertical, { className: "w-4 h-4", style: { color: 'var(--text-tertiary)' } }) }), isExpanded ? (_jsx(ChevronDown, { className: "w-4 h-4", style: { color: 'var(--text-primary)' } })) : (_jsx(ChevronRight, { className: "w-4 h-4", style: { color: 'var(--text-primary)' } })), _jsx("div", { className: "w-1 h-8 rounded-full", style: { backgroundColor: category.color } }), _jsx("span", { className: "text-sm font-medium uppercase tracking-wide", style: { color: 'var(--text-primary)' }, children: category.name })] }), calendarDates.map((dateInfo) => (_jsx("div", { className: "w-16 flex-shrink-0", style: { borderRight: '1px solid var(--border-color)', backgroundColor: 'var(--bg-elevated)' } }, `group-${category.id}-${dateInfo.date}`))), _jsxs("div", { className: "flex-shrink-0 flex items-center", style: { backgroundColor: 'var(--bg-elevated)' }, children: [_jsx("div", { className: "w-20 px-2 text-center", style: { borderRight: '1px solid var(--border-color)' }, children: _jsxs("div", { className: "text-sm font-medium", style: { color: 'var(--text-primary)' }, children: [stats.currentStreak, "\uD83D\uDD25"] }) }), _jsx("div", { className: "w-20 px-2 text-center", style: { borderRight: '1px solid var(--border-color)' }, children: _jsxs("div", { className: "text-sm font-medium", style: { color: 'var(--text-primary)' }, children: [stats.completionPercentage, "%"] }) }), _jsx("div", { className: "w-20 px-2 text-center", children: _jsx("div", { className: "text-sm font-medium", style: { color: 'var(--text-primary)' }, children: category.habits.length }) })] })] }));
}
export function HabitsApp() {
    const { user } = useAuth();
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [showAddHabit, setShowAddHabit] = useState(false);
    const [calendarDays, setCalendarDays] = useState(14);
    const [selectedHabit, setSelectedHabit] = useState(null);
    const [groupOrder, setGroupOrder] = useState([]);
    // Add Habit Modal State
    const [newHabitLabel, setNewHabitLabel] = useState('');
    const [newHabitDescription, setNewHabitDescription] = useState('');
    const [newHabitColor, setNewHabitColor] = useState('#00B7EB');
    const [newHabitTime, setNewHabitTime] = useState('08:00');
    const [newHabitGroup, setNewHabitGroup] = useState('');
    const [newHabitFrequency, setNewHabitFrequency] = useState('Every day!');
    const calendarDates = useMemo(() => generateCalendarDates(calendarDays), [calendarDays]);
    const sensors = useSensors(useSensor(PointerSensor), useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
    }));
    const loadHabits = useCallback(async () => {
        if (!user)
            return;
        try {
            // Fetch habits from steps table (legacy format)
            const { data: stepsData, error: stepsError } = await supabase
                .from('steps')
                .select('*')
                .eq('user_id', user.id)
                .eq('tag', 'habit')
                .order('created_at', { ascending: true });
            if (stepsError)
                throw stepsError;
            // Fetch habit_groups from lengths table
            const { data: habitGroupsData, error: groupsError } = await supabase
                .from('lengths')
                .select('*')
                .eq('user_id', user.id)
                .eq('type', 'habit_group')
                .order('created_at', { ascending: true });
            if (groupsError)
                throw groupsError;
            // Convert steps to habits
            const habits = (stepsData || []).map((step) => ({
                id: step.id,
                label: step.label,
                description: step.description || '',
                color: step.color || '#00B7EB',
                time: step.habit_notes?._scheduled_time || '08:00',
                frequency: step.frequency || 'Every day!',
                isBuildHabit: step.isbuildhabit ?? true,
                habitGroup: step.habit_group || null,
                lengthId: step.length_id || null,
                history: step.history || [],
                notes: step.habit_notes || {},
                cbtNotes: step.cbt_notes || {},
                units: step.habit_units || {},
                skipped: step.habit_skipped || {}
            }));
            const uncategorizedHabits = [];
            const groupedHabits = {};
            habits.forEach((habit) => {
                if (!habit.habitGroup) {
                    uncategorizedHabits.push(habit);
                }
                else {
                    if (!groupedHabits[habit.habitGroup]) {
                        groupedHabits[habit.habitGroup] = [];
                    }
                    groupedHabits[habit.habitGroup].push(habit);
                }
            });
            const newCategories = [];
            if (uncategorizedHabits.length > 0) {
                newCategories.push({
                    id: 'uncategorized',
                    name: 'Uncategorized',
                    color: '#808080',
                    habits: uncategorizedHabits
                });
            }
            // Add habit groups from task_lists table
            // Match habits with these groups and populate their habits array
            (habitGroupsData || []).forEach((group) => {
                const groupHabits = groupedHabits[group.name] || [];
                newCategories.push({
                    id: `habit-group-${group.id}`,
                    name: group.name,
                    color: groupHabits[0]?.color || '#F97316', // Use first habit's color if available
                    habits: groupHabits
                });
            });
            // Add any remaining grouped habits that don't have a task_lists entry yet
            // (in case of edge cases or new groups created before migration)
            const taskListGroupNames = new Set((habitGroupsData || []).map((g) => g.name));
            Object.entries(groupedHabits).forEach(([groupName, habits]) => {
                // Only add if this group isn't already in task_lists
                if (!taskListGroupNames.has(groupName)) {
                    const firstHabitColor = habits[0]?.color || '#00B7EB';
                    newCategories.push({
                        id: `group-${groupName}`,
                        name: groupName,
                        color: firstHabitColor,
                        habits
                    });
                }
            });
            // Load saved group order from localStorage
            const savedOrder = localStorage.getItem(`habit-group-order-${user.id}`);
            if (savedOrder) {
                try {
                    const orderArray = JSON.parse(savedOrder);
                    setGroupOrder(orderArray);
                    // Sort categories based on saved order
                    newCategories.sort((a, b) => {
                        const indexA = orderArray.indexOf(a.id);
                        const indexB = orderArray.indexOf(b.id);
                        // If both are in the order array, sort by their position
                        if (indexA !== -1 && indexB !== -1) {
                            return indexA - indexB;
                        }
                        // If only one is in the order array, put it first
                        if (indexA !== -1)
                            return -1;
                        if (indexB !== -1)
                            return 1;
                        // If neither is in the order array, keep original order
                        return 0;
                    });
                }
                catch (e) {
                    console.error('Error parsing group order:', e);
                }
            }
            else {
                // Initialize group order with current category IDs
                setGroupOrder(newCategories.map(c => c.id));
            }
            setCategories(newCategories);
            setLoading(false);
        }
        catch (error) {
            console.error('Error loading habits:', error);
            setLoading(false);
        }
    }, [user]);
    useEffect(() => {
        loadHabits();
    }, [loadHabits]);
    // Three-state completion: incomplete → completed → skipped → incomplete
    const toggleCompletion = async (habit, dateStr) => {
        if (!user)
            return;
        const updatedHistory = [...(habit.history || [])];
        const updatedSkipped = { ...(habit.skipped || {}) };
        const isCompleted = updatedHistory.includes(dateStr);
        const isSkipped = updatedSkipped[dateStr];
        if (isCompleted) {
            // Completed → Skipped
            const index = updatedHistory.indexOf(dateStr);
            updatedHistory.splice(index, 1);
            updatedSkipped[dateStr] = true;
        }
        else if (isSkipped) {
            // Skipped → Incomplete
            delete updatedSkipped[dateStr];
        }
        else {
            // Incomplete → Completed
            updatedHistory.push(dateStr);
            delete updatedSkipped[dateStr];
        }
        const { error } = await supabase
            .from('steps')
            .update({
            history: updatedHistory,
            habit_skipped: updatedSkipped
        })
            .eq('id', habit.id);
        if (error) {
            console.error('Error updating habit:', error);
        }
        else {
            loadHabits();
        }
    };
    const addHabit = async () => {
        if (!user || !newHabitLabel.trim())
            return;
        try {
            const { error } = await supabase
                .from('steps')
                .insert([
                {
                    label: newHabitLabel,
                    description: newHabitDescription,
                    tag: 'habit',
                    color: newHabitColor,
                    frequency: newHabitFrequency,
                    completed: false,
                    isbuildhabit: true,
                    user_id: user.id,
                    habit_group: newHabitGroup || null,
                    habit_notes: {
                        _scheduled_time: newHabitTime
                    },
                    history: [],
                    habit_skipped: {},
                    habit_units: {}
                }
            ]);
            if (error)
                throw error;
            setShowAddHabit(false);
            setNewHabitLabel('');
            setNewHabitDescription('');
            setNewHabitColor('#00B7EB');
            setNewHabitTime('08:00');
            setNewHabitGroup('');
            setNewHabitFrequency('Every day!');
            loadHabits();
        }
        catch (error) {
            console.error('Error adding habit:', error);
        }
    };
    const deleteHabit = async (habitId) => {
        if (!user)
            return;
        try {
            const { error } = await supabase
                .from('steps')
                .delete()
                .eq('id', habitId);
            if (error)
                throw error;
            loadHabits();
        }
        catch (error) {
            console.error('Error deleting habit:', error);
        }
    };
    const updateHabit = async (habitId, updates) => {
        if (!user)
            return;
        const dbUpdates = {};
        if (updates.label !== undefined)
            dbUpdates.label = updates.label;
        if (updates.description !== undefined)
            dbUpdates.description = updates.description;
        if (updates.color !== undefined)
            dbUpdates.color = updates.color;
        if (updates.habitGroup !== undefined)
            dbUpdates.habit_group = updates.habitGroup;
        if (updates.frequency !== undefined)
            dbUpdates.frequency = updates.frequency;
        if (updates.history !== undefined)
            dbUpdates.history = updates.history;
        if (updates.skipped !== undefined)
            dbUpdates.habit_skipped = updates.skipped;
        if (updates.notes !== undefined)
            dbUpdates.habit_notes = updates.notes;
        if (updates.cbtNotes !== undefined)
            dbUpdates.cbt_notes = updates.cbtNotes;
        if (updates.units !== undefined)
            dbUpdates.habit_units = updates.units;
        if (updates.time !== undefined) {
            const currentHabit = categories
                .flatMap(c => c.habits)
                .find(h => h.id === habitId);
            if (currentHabit) {
                dbUpdates.habit_notes = {
                    ...(currentHabit.notes || {}),
                    _scheduled_time: updates.time
                };
            }
        }
        try {
            const { error } = await supabase
                .from('steps')
                .update(dbUpdates)
                .eq('id', habitId);
            if (error)
                throw error;
            loadHabits();
        }
        catch (error) {
            console.error('Error updating habit:', error);
        }
    };
    const toggleGroup = (groupId) => {
        const newExpanded = new Set(expandedGroups);
        if (newExpanded.has(groupId)) {
            newExpanded.delete(groupId);
        }
        else {
            newExpanded.add(groupId);
        }
        setExpandedGroups(newExpanded);
    };
    const calculateGroupStats = (habits) => {
        const today = new Date().toISOString().split('T')[0];
        const totalPossible = habits.length;
        const completedToday = habits.filter(h => h.history?.includes(today)).length;
        const completionPercentage = totalPossible > 0 ? Math.round((completedToday / totalPossible) * 100) : 0;
        const streaks = habits.map(h => calculateCurrentStreak(h.history || [], h.skipped));
        const currentStreak = streaks.length > 0 ? Math.max(...streaks) : 0;
        return { completionPercentage, currentStreak };
    };
    const allHabits = useMemo(() => {
        return categories.flatMap(c => c.habits);
    }, [categories]);
    const handleHabitClick = (habit) => {
        setSelectedHabit(habit);
    };
    const handleNextHabit = () => {
        if (!selectedHabit)
            return;
        const currentIndex = allHabits.findIndex(h => h.id === selectedHabit.id);
        if (currentIndex < allHabits.length - 1) {
            setSelectedHabit(allHabits[currentIndex + 1]);
        }
    };
    const handlePreviousHabit = () => {
        if (!selectedHabit)
            return;
        const currentIndex = allHabits.findIndex(h => h.id === selectedHabit.id);
        if (currentIndex > 0) {
            setSelectedHabit(allHabits[currentIndex - 1]);
        }
    };
    const handleNavigateToIndex = (index) => {
        if (index >= 0 && index < allHabits.length) {
            setSelectedHabit(allHabits[index]);
        }
    };
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id)
            return;
        setCategories((items) => {
            const oldIndex = items.findIndex((item) => item.id === active.id);
            const newIndex = items.findIndex((item) => item.id === over.id);
            if (oldIndex === -1 || newIndex === -1)
                return items;
            const newOrder = arrayMove(items, oldIndex, newIndex);
            const newOrderIds = newOrder.map(c => c.id);
            // Save to localStorage
            if (user) {
                localStorage.setItem(`habit-group-order-${user.id}`, JSON.stringify(newOrderIds));
                setGroupOrder(newOrderIds);
            }
            return newOrder;
        });
    };
    if (loading) {
        return (_jsx("div", { className: "flex items-center justify-center h-full", style: { backgroundColor: 'var(--bg-base)' }, children: _jsx("div", { className: "text-lg", style: { color: 'var(--text-secondary)' }, children: "Loading habits..." }) }));
    }
    return (_jsxs("div", { className: "flex flex-col h-full", style: { backgroundColor: 'var(--bg-base)' }, children: [_jsxs("div", { className: "flex items-center justify-between px-8 py-6", style: { borderBottom: '1px solid var(--border-color)' }, children: [_jsxs("div", { children: [_jsx("h1", { className: "text-3xl font-light", style: { color: 'var(--text-primary)' }, children: "Habits" }), _jsx("p", { className: "mt-1 text-sm", style: { color: 'var(--text-secondary)' }, children: "Track your daily habits and build consistency" })] }), _jsxs("div", { className: "flex items-center gap-4", children: [_jsxs("select", { value: calendarDays, onChange: (e) => setCalendarDays(Number(e.target.value)), className: "px-4 py-2 rounded-lg text-sm focus:outline-none", style: {
                                    backgroundColor: 'var(--bg-elevated)',
                                    color: 'var(--text-primary)',
                                    border: '1px solid var(--border-color)'
                                }, children: [_jsx("option", { value: 7, children: "7 days" }), _jsx("option", { value: 14, children: "14 days" }), _jsx("option", { value: 30, children: "30 days" }), _jsx("option", { value: 90, children: "90 days" })] }), _jsxs("button", { onClick: () => setShowAddHabit(true), className: "px-6 py-2 rounded-lg font-medium flex items-center gap-2 transition-all hover:scale-105", style: { backgroundColor: 'var(--cyan-bright)', color: 'white' }, children: [_jsx(Plus, { className: "w-5 h-5" }), "Add Habit"] })] })] }), _jsx("div", { className: "flex-1 overflow-auto", children: _jsxs("div", { className: "inline-flex flex-col min-w-full", children: [_jsxs("div", { className: "sticky top-0 z-20 flex", style: { backgroundColor: 'var(--bg-base)' }, children: [_jsx("div", { className: "w-64 flex-shrink-0 px-4 py-3 font-medium", style: { color: 'var(--text-primary)', borderBottom: '1px solid var(--border-color)', borderRight: '1px solid var(--border-color)' }, children: "Habit" }), calendarDates.map((dateInfo) => (_jsxs("div", { className: "w-16 flex-shrink-0 px-2 py-3 text-center", style: {
                                        backgroundColor: dateInfo.isToday ? 'var(--cyan-bright)' : 'transparent',
                                        color: dateInfo.isToday ? 'white' : 'var(--text-secondary)',
                                        borderBottom: '1px solid var(--border-color)',
                                        borderRight: '1px solid var(--border-color)'
                                    }, children: [_jsx("div", { className: "text-xs font-medium", children: dateInfo.dayName }), _jsx("div", { className: "text-sm", children: dateInfo.day })] }, dateInfo.date))), _jsxs("div", { className: "flex-shrink-0 flex", children: [_jsx("div", { className: "w-20 px-2 py-3 text-center text-xs font-medium", style: { color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-color)', borderRight: '1px solid var(--border-color)' }, children: "Streak" }), _jsx("div", { className: "w-20 px-2 py-3 text-center text-xs font-medium", style: { color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-color)', borderRight: '1px solid var(--border-color)' }, children: "Rate" }), _jsx("div", { className: "w-20 px-2 py-3 text-center text-xs font-medium", style: { color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-color)' }, children: "Count" })] })] }), _jsx(DndContext, { sensors: sensors, collisionDetection: closestCenter, onDragEnd: handleDragEnd, children: _jsx(SortableContext, { items: categories.map(c => c.id), strategy: verticalListSortingStrategy, children: categories.map((category) => {
                                    const isUncategorized = category.id === 'uncategorized';
                                    const isExpanded = expandedGroups.has(category.id);
                                    const stats = calculateGroupStats(category.habits);
                                    return (_jsxs("div", { children: [!isUncategorized && (_jsx("div", { style: { borderBottom: '1px solid var(--border-color)' }, children: _jsx(SortableGroupHeader, { category: category, isExpanded: isExpanded, stats: stats, calendarDates: calendarDates, onToggle: () => toggleGroup(category.id) }) })), (isUncategorized || isExpanded) &&
                                                category.habits.map((habit) => {
                                                    const currentStreak = calculateCurrentStreak(habit.history || [], habit.skipped);
                                                    const totalCount = habit.history?.length || 0;
                                                    const completionRate = calendarDates.length > 0
                                                        ? Math.round((calendarDates.filter(d => habit.history?.includes(d.date)).length / calendarDates.length) * 100)
                                                        : 0;
                                                    return (_jsxs("div", { className: "flex items-center hover:bg-opacity-50 transition-colors", style: { borderBottom: '1px solid var(--border-color)' }, children: [_jsxs("div", { onClick: () => handleHabitClick(habit), className: "w-64 flex-shrink-0 px-4 py-3 flex items-center gap-2 cursor-pointer", style: { borderRight: '1px solid var(--border-color)' }, children: [_jsx(GripVertical, { className: "w-4 h-4 opacity-40", style: { color: 'var(--text-tertiary)' } }), _jsx("div", { className: "w-1 h-6 rounded-full", style: { backgroundColor: habit.color } }), _jsx("span", { className: "text-sm flex-1 truncate", style: { color: 'var(--text-primary)' }, children: habit.label })] }), calendarDates.map((dateInfo) => {
                                                                const isCompleted = habit.history?.includes(dateInfo.date);
                                                                const isSkipped = habit.skipped?.[dateInfo.date];
                                                                const colors = getColorWithSaturation(habit.color, habit, dateInfo.date);
                                                                return (_jsxs("button", { onClick: () => toggleCompletion(habit, dateInfo.date), className: "w-16 h-16 flex-shrink-0 flex items-center justify-center relative overflow-hidden transition-all hover:opacity-80", style: {
                                                                        backgroundColor: colors.backgroundColor,
                                                                        borderRight: '1px solid var(--border-color)'
                                                                    }, children: [isSkipped && (_jsx("div", { className: "absolute inset-0", style: {
                                                                                backgroundImage: `linear-gradient(
                                      to top right,
                                      ${habit.color} 0%,
                                      ${habit.color} 50%,
                                      transparent 50%,
                                      transparent 100%
                                    )`,
                                                                            } })), isCompleted && (_jsx("div", { className: "w-2 h-2 bg-white rounded-full relative z-10" })), isSkipped && (_jsx(X, { className: "w-4 h-4 text-white relative z-10" }))] }, `${habit.id}-${dateInfo.date}`));
                                                            }), _jsxs("div", { className: "flex-shrink-0 flex items-center", children: [_jsx("div", { className: "w-20 px-2 text-center", style: { borderRight: '1px solid var(--border-color)' }, children: _jsx("div", { className: "text-sm font-medium", style: { color: 'var(--text-primary)' }, children: currentStreak }) }), _jsx("div", { className: "w-20 px-2 text-center", style: { borderRight: '1px solid var(--border-color)' }, children: _jsxs("div", { className: "text-sm font-medium", style: { color: 'var(--text-primary)' }, children: [completionRate, "%"] }) }), _jsx("div", { className: "w-20 px-2 text-center", children: _jsx("div", { className: "text-sm font-medium", style: { color: 'var(--text-primary)' }, children: totalCount }) })] })] }, habit.id));
                                                })] }, category.id));
                                }) }) })] }) }), showAddHabit && (_jsx("div", { className: "fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4", children: _jsxs("div", { className: "w-full max-w-lg rounded-2xl p-6", style: { backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-color)' }, children: [_jsxs("div", { className: "flex items-center justify-between mb-6", children: [_jsx("h2", { className: "text-2xl font-semibold", style: { color: 'var(--text-primary)' }, children: "Add New Habit" }), _jsx("button", { onClick: () => setShowAddHabit(false), className: "p-2 rounded-lg transition-colors", style: { backgroundColor: 'var(--bg-base)' }, children: _jsx(X, { className: "w-5 h-5", style: { color: 'var(--text-primary)' } }) })] }), _jsxs("div", { className: "space-y-4", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-primary)' }, children: "Habit Name" }), _jsx("input", { type: "text", value: newHabitLabel, onChange: (e) => setNewHabitLabel(e.target.value), className: "w-full px-4 py-3 rounded-lg focus:outline-none", style: {
                                                backgroundColor: 'var(--bg-base)',
                                                color: 'var(--text-primary)',
                                                border: '1px solid var(--border-color)'
                                            }, placeholder: "e.g., Morning Meditation" })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-primary)' }, children: "Description" }), _jsx("textarea", { value: newHabitDescription, onChange: (e) => setNewHabitDescription(e.target.value), className: "w-full px-4 py-3 rounded-lg focus:outline-none resize-none", style: {
                                                backgroundColor: 'var(--bg-base)',
                                                color: 'var(--text-primary)',
                                                border: '1px solid var(--border-color)'
                                            }, rows: 3, placeholder: "Optional description..." })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-primary)' }, children: "Color" }), _jsx("div", { className: "grid grid-cols-8 gap-2", children: colorOptions.map((color) => (_jsx("button", { onClick: () => setNewHabitColor(color.value), className: `w-10 h-10 rounded-lg transition-all ${newHabitColor === color.value ? 'ring-2 scale-110' : 'hover:scale-110'}`, style: {
                                                    backgroundColor: color.value,
                                                    ringColor: 'var(--cyan-bright)'
                                                } }, color.value))) })] }), _jsxs("div", { className: "grid grid-cols-2 gap-4", children: [_jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-primary)' }, children: "Time" }), _jsx("input", { type: "time", value: newHabitTime, onChange: (e) => setNewHabitTime(e.target.value), className: "w-full px-4 py-3 rounded-lg font-mono focus:outline-none", style: {
                                                        backgroundColor: 'var(--bg-base)',
                                                        color: 'var(--text-primary)',
                                                        border: '1px solid var(--border-color)'
                                                    } })] }), _jsxs("div", { children: [_jsx("label", { className: "block text-sm font-medium mb-2", style: { color: 'var(--text-primary)' }, children: "Group" }), _jsx("input", { type: "text", value: newHabitGroup, onChange: (e) => setNewHabitGroup(e.target.value), className: "w-full px-4 py-3 rounded-lg focus:outline-none", style: {
                                                        backgroundColor: 'var(--bg-base)',
                                                        color: 'var(--text-primary)',
                                                        border: '1px solid var(--border-color)'
                                                    }, placeholder: "e.g., Morning" })] })] }), _jsxs("div", { className: "flex gap-3 pt-4", children: [_jsx("button", { onClick: () => setShowAddHabit(false), className: "flex-1 px-4 py-3 rounded-lg font-medium transition-all", style: {
                                                backgroundColor: 'var(--bg-base)',
                                                color: 'var(--text-primary)',
                                                border: '1px solid var(--border-color)'
                                            }, children: "Cancel" }), _jsx("button", { onClick: addHabit, className: "flex-1 px-4 py-3 rounded-lg font-medium transition-all hover:scale-105", style: { backgroundColor: 'var(--cyan-bright)', color: 'white' }, disabled: !newHabitLabel.trim(), children: "Add Habit" })] })] })] }) })), selectedHabit && (_jsx(HabitDetailModal, { habit: selectedHabit, allHabits: allHabits, onClose: () => setSelectedHabit(null), onNext: handleNextHabit, onPrevious: handlePreviousHabit, onNavigateToIndex: handleNavigateToIndex, onUpdate: updateHabit, onDelete: deleteHabit }))] }));
}
